/**
 * Shared "is this intake ready for the decision engine?" computation.
 *
 * This is deliberately implemented as a pure function: the doctor portal
 * uses it to show a live readiness panel during the interview, and the ops
 * portal uses it to sort the intake queue and drive the medical-director
 * sign-off queue. Both portals must reach the same verdict for the same
 * input — so this file is the single source of truth.
 */

import type {
  PatientFamilyHistoryIntakeRecord,
  IntakeRelativeRecord,
} from './familyHistoryIntake';
import { RELATION_CATALOG } from './intakeVocabulary';

export type ReadinessSeverity = 'blocker' | 'warning' | 'info';

export interface ReadinessCheck {
  id: string;
  severity: ReadinessSeverity;
  label: string;
  detail?: string;
  /** Optional short hint to guide the clinician toward resolution. */
  remediation?: string;
}

export interface IntakeReadinessVerdict {
  /** `true` only if no blockers remain. Warnings do not gate release. */
  ready: boolean;
  /** 0\u20131 proportion of required checks that have passed. */
  completionRatio: number;
  blockers: ReadinessCheck[];
  warnings: ReadinessCheck[];
  info: ReadinessCheck[];
  /** Count of required checks passed vs total \u2014 for display. */
  requiredPassed: number;
  requiredTotal: number;
}

interface CheckDefinition {
  id: string;
  severity: ReadinessSeverity;
  label: string;
  remediation?: string;
  evaluate: (intake: PatientFamilyHistoryIntakeRecord) => { passed: boolean; detail?: string };
}

const PERSONAL_REQUIRED: CheckDefinition[] = [
  {
    id: 'personal.dob',
    severity: 'blocker',
    label: 'Date of birth captured',
    remediation: 'Ask the patient in the interview and save it on the personal-info tab.',
    evaluate: (intake) => ({ passed: Boolean(intake.personalInfo?.dateOfBirth?.trim?.()) }),
  },
  {
    id: 'personal.sex',
    severity: 'blocker',
    label: 'Sex at birth captured',
    remediation: 'Several rules (breast/ovarian, prostate) depend on sex at birth.',
    evaluate: (intake) => ({ passed: Boolean(intake.personalInfo?.sexAtBirth?.trim?.()) }),
  },
];

const FAMILY_REQUIRED: CheckDefinition[] = [
  {
    id: 'family.mother',
    severity: 'blocker',
    label: 'Mother captured (alive, deceased, or explicitly unknown)',
    remediation: 'Add the mother as a relative, even if just to record "unknown".',
    evaluate: (intake) => ({ passed: hasRelation(intake.relatives, 'mother') }),
  },
  {
    id: 'family.father',
    severity: 'blocker',
    label: 'Father captured (alive, deceased, or explicitly unknown)',
    remediation: 'Add the father as a relative, even if just to record "unknown".',
    evaluate: (intake) => ({ passed: hasRelation(intake.relatives, 'father') }),
  },
  {
    id: 'family.conditions_coded',
    severity: 'blocker',
    label: 'All relative conditions have a controlled code',
    remediation: 'Open each relative\u2019s conditions and pick a code from the dropdown.',
    evaluate: (intake) => {
      const uncoded: string[] = [];
      intake.relatives.forEach((relative) => {
        relative.conditions.forEach((condition) => {
          if (!condition.code && condition.label?.trim?.()) {
            uncoded.push(`${relative.relationLabel || relative.relation}: ${condition.label}`);
          }
        });
      });
      return {
        passed: uncoded.length === 0,
        detail: uncoded.length > 0 ? `${uncoded.length} condition entr${uncoded.length === 1 ? 'y' : 'ies'} still uncoded.` : undefined,
      };
    },
  },
];

const FAMILY_RECOMMENDED: CheckDefinition[] = [
  {
    id: 'family.maternal_grandparent',
    severity: 'warning',
    label: 'At least one maternal grandparent captured',
    remediation: 'Needed to search for lineage-specific cancer and dementia patterns.',
    evaluate: (intake) => ({
      passed: hasRelation(intake.relatives, 'maternal_grandmother') || hasRelation(intake.relatives, 'maternal_grandfather') || hasLineage(intake.relatives, 'maternal', 2),
    }),
  },
  {
    id: 'family.paternal_grandparent',
    severity: 'warning',
    label: 'At least one paternal grandparent captured',
    remediation: 'Needed to search for lineage-specific cancer and dementia patterns.',
    evaluate: (intake) => ({
      passed: hasRelation(intake.relatives, 'paternal_grandmother') || hasRelation(intake.relatives, 'paternal_grandfather') || hasLineage(intake.relatives, 'paternal', 2),
    }),
  },
  {
    id: 'family.siblings_covered',
    severity: 'warning',
    label: 'Sibling context captured (count or explicit none)',
    remediation: 'Set sibling count on the family-structure tab, or add siblings as relatives.',
    evaluate: (intake) => {
      const siblingCount = intake.familyStructure?.siblingsCount;
      const hasSiblings = intake.relatives.some((relative) => ['sister', 'brother', 'half_sister', 'half_brother'].includes(relative.relation));
      return { passed: siblingCount != null || hasSiblings };
    },
  },
  {
    id: 'family.pattern_flags_reviewed',
    severity: 'warning',
    label: 'Pattern flags reviewed (none left as "suspected")',
    remediation: 'Confirm, rule out, or mark each auto-suggested pattern flag as needing more info.',
    evaluate: (intake) => {
      const flags = intake.patternFlags || [];
      const suspected = flags.filter((flag) => flag.status === 'suspected');
      return {
        passed: suspected.length === 0,
        detail: suspected.length > 0 ? `${suspected.length} flag${suspected.length === 1 ? '' : 's'} still marked suspected.` : undefined,
      };
    },
  },
];

const INFO_CHECKS: CheckDefinition[] = [
  {
    id: 'family.cause_of_death',
    severity: 'info',
    label: 'Cause of death noted for any deceased relative',
    remediation: 'Improves cardiovascular rule precision but not required for the engine to run.',
    evaluate: (intake) => {
      const deceased = intake.relatives.filter((relative) => relative.aliveStatus === 'deceased');
      if (deceased.length === 0) return { passed: true };
      const withCause = deceased.filter((relative) => relative.causeOfDeath?.trim?.());
      return {
        passed: withCause.length === deceased.length,
        detail: `${withCause.length}/${deceased.length} deceased relatives have cause of death captured.`,
      };
    },
  },
];

const ALL_CHECKS = [...PERSONAL_REQUIRED, ...FAMILY_REQUIRED, ...FAMILY_RECOMMENDED, ...INFO_CHECKS];

export function computeIntakeReadiness(intake: PatientFamilyHistoryIntakeRecord | null | undefined): IntakeReadinessVerdict {
  if (!intake) {
    return {
      ready: false,
      completionRatio: 0,
      blockers: [],
      warnings: [],
      info: [],
      requiredPassed: 0,
      requiredTotal: [...PERSONAL_REQUIRED, ...FAMILY_REQUIRED].length,
    };
  }

  const blockers: ReadinessCheck[] = [];
  const warnings: ReadinessCheck[] = [];
  const info: ReadinessCheck[] = [];
  const required = [...PERSONAL_REQUIRED, ...FAMILY_REQUIRED];
  let requiredPassed = 0;

  ALL_CHECKS.forEach((check) => {
    const { passed, detail } = check.evaluate(intake);
    if (passed) {
      if (required.includes(check)) requiredPassed += 1;
      return;
    }
    const failure: ReadinessCheck = {
      id: check.id,
      severity: check.severity,
      label: check.label,
      detail,
      remediation: check.remediation,
    };
    if (check.severity === 'blocker') blockers.push(failure);
    else if (check.severity === 'warning') warnings.push(failure);
    else info.push(failure);
  });

  return {
    ready: blockers.length === 0,
    completionRatio: required.length === 0 ? 1 : requiredPassed / required.length,
    blockers,
    warnings,
    info,
    requiredPassed,
    requiredTotal: required.length,
  };
}

function hasRelation(relatives: IntakeRelativeRecord[], relation: keyof typeof RELATION_CATALOG): boolean {
  return relatives.some((relative) => relative.relation === relation);
}

function hasLineage(relatives: IntakeRelativeRecord[], lineage: 'maternal' | 'paternal', degree: 1 | 2 | 3): boolean {
  return relatives.some((relative) => {
    const meta = RELATION_CATALOG[relative.relation];
    if (!meta) return false;
    return meta.lineage === lineage && meta.degree === degree;
  });
}
